import React, { createContext, ReactNode, useEffect, useReducer } from 'react';
import {
  createShortLivedToken,
  EXPIRATION_SECONDS,
} from '../services/vault/token';
import useVgsShow from '../hooks/useVgsShowInstance';
import { VgsFieldProvider } from './VgsField';

export type VgsShowContextState = {
  token?: {
    value: string;
    validUntil: Date;
  };
  vgsShowInstance?: VGSShowInstance;
};

export type VgsShowAction = { type: 'SET_TOKEN'; value: string };

const VgsShowContext = createContext<{
  state: VgsShowContextState;
  dispatch: React.Dispatch<VgsShowAction>;
  getFreshToken: () => Promise<string>;
  vgsShowInstance?: VGSShowInstance;
}>({
  state: {},
  dispatch: () => false,
  getFreshToken: () => Promise.resolve(''),
});

export default VgsShowContext;

function vgsShowReducer(
  state: VgsShowContextState,
  action: VgsShowAction,
): VgsShowContextState {
  switch (action.type) {
    case 'SET_TOKEN': {
      const now = new Date();
      const validUntil = new Date(now.getTime() + EXPIRATION_SECONDS * 1000);
      return {
        ...state,
        token: {
          value: action.value,
          validUntil,
        },
      };
    }
    default:
      return state;
  }
}

export function VgsShowProvider({
  children,
  visible,
  loadingElement,
}: {
  children: ReactNode | ReactNode[];
  visible?: boolean;
  loadingElement?: ReactNode;
}) {
  const [state, dispatch] = useReducer(vgsShowReducer, {});

  const vgsShowInstance = useVgsShow();

  const getAndSetFreshToken = async () => {
    const tokenString = await createShortLivedToken();
    dispatch({ type: 'SET_TOKEN', value: tokenString });
    return tokenString;
  };

  const getFreshToken = async (): Promise<string> => {
    // token is not set,
    // set and return
    if (!state.token) {
      return getAndSetFreshToken();
    }
    const now = new Date();
    // token is no longer valid
    if (now.getTime() > state.token.validUntil.getTime()) {
      // set new one and return it
      return getAndSetFreshToken();
    } else {
      // otherwise return current token
      return state.token.value;
    }
  };

  useEffect(() => {
    if (state.token) {
      return;
    }
    getAndSetFreshToken();
  }, [state.token]);
  return (
    <VgsShowContext.Provider
      value={{
        state,
        dispatch,
        getFreshToken,
        vgsShowInstance,
      }}
    >
      <VgsFieldProvider visible={visible} loadingElement={loadingElement}>
        {children}
      </VgsFieldProvider>
    </VgsShowContext.Provider>
  );
}
